/**
 * SandboxSaveModal — signup/login modal for saving sandbox work.
 * Creates a sandbox_user account with Google OAuth or email/password.
 */

import React, { useState } from 'react';
import { X, Save, FlaskConical } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { isSupabaseConfigured } from '../config/supabase';

export default function SandboxSaveModal({ onClose, onSaved }) {
  const [mode, setMode] = useState('signup');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const { signUp, signIn, signInWithGoogle } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (mode === 'signup') {
        const result = await signUp(email, password, { name, sandbox_origin: true });
        if (result.session) {
          setSuccess(true);
          onSaved?.();
        } else {
          setSuccess(true);
        }
      } else {
        await signIn(email, password);
        setSuccess(true);
        onSaved?.();
      }
    } catch (err) {
      setError(err.message || 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setLoading(true);
    setError(null);
    try {
      sessionStorage.setItem('sandbox_pending_save', 'true');
      await signInWithGoogle();
    } catch (err) {
      setError(err.message || 'Google sign-in failed.');
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="sb-modal-overlay" onClick={onClose}>
        <div className="sb-save-modal" onClick={e => e.stopPropagation()}>
          <button className="sb-modal-close" onClick={onClose} aria-label="Close"><X size={18} /></button>
          <div className="sb-save-success">
            <Save size={40} />
            <h2>Your work is saved</h2>
            <p>Sign in anytime to pick up where you left off.</p>
            <button className="sb-save-btn sb-save-btn--primary" onClick={onClose}>Continue</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sb-modal-overlay" onClick={onClose}>
      <div className="sb-save-modal" onClick={e => e.stopPropagation()}>
        <button className="sb-modal-close" onClick={onClose} aria-label="Close"><X size={18} /></button>

        <div className="sb-save-header">
          <FlaskConical size={24} />
          <h2>{mode === 'signup' ? 'Save Your Sandbox Work' : 'Sign In to Load Your Work'}</h2>
          <p className="sb-save-subtitle">
            Create a free account to save your assessments. Sandbox accounts only store your sandbox data.
          </p>
        </div>

        {error && <div className="sb-save-error">{error}</div>}

        <form onSubmit={handleSubmit} className="sb-save-form">
          {mode === 'signup' && (
            <input type="text" placeholder="Full Name" value={name} onChange={e => setName(e.target.value)} className="sb-save-input" disabled={loading} />
          )}
          <input type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} required className="sb-save-input" disabled={loading} />
          <input type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} required className="sb-save-input" disabled={loading} minLength={mode === 'signup' ? 6 : undefined} />
          <button type="submit" disabled={loading || !isSupabaseConfigured} className="sb-save-btn sb-save-btn--primary">
            <Save size={16} />
            {loading ? 'Saving...' : mode === 'signup' ? 'Create Account & Save' : 'Sign In & Save'}
          </button>
        </form>

        <div className="sb-save-divider">Or continue with</div>

        <button onClick={handleGoogle} disabled={loading || !isSupabaseConfigured} className="sb-save-btn sb-save-btn--oauth" type="button">
          <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
          Sign {mode === 'signup' ? 'up' : 'in'} with Google
        </button>

        <div className="sb-save-switch">
          {mode === 'signup' ? (
            <p>Already have an account? <button onClick={() => setMode('signin')} className="sb-save-link">Sign in</button></p>
          ) : (
            <p>Need an account? <button onClick={() => setMode('signup')} className="sb-save-link">Create one</button></p>
          )}
        </div>

        <div className="sb-save-notice">
          Sandbox accounts can only save and load sandbox assessments. No access to organizational data or platform features.
        </div>
      </div>
    </div>
  );
}
